/**
 * roleRegistry.js — Grown-up Mode role → canonical destination registry (Jun 2026).
 * Reuses EXISTING adult routes only. `nav` is the permission-aware adult navigation for that role.
 * status: "live" | "beta" — never fabricate a working dashboard.
 * Gate: all roles pass the existing GrownupPinGate ("math") once per session; sensitive writes stay
 * server-authorised (e.g. school admin endpoints require X-Admin-PIN on the backend).
 */
export const ROLES = [
  {
    id: "parent", label: "Parent or carer", short: "Parent",
    promise: "See progress, plan the week, and find a helpful next step.",
    to: "/parents/dashboard", status: "live",
    nav: [
      { label: "Overview", to: "/parents/dashboard" },
      { label: "Learning", to: "/my-learning" },
      { label: "Plan", to: "/parents/plan" },
      { label: "Reports", to: "/parents/report" },
      { label: "Settings", to: "/grown-ups/settings" },
      { label: "Printables", to: "/printables" },
      { label: "Real Teachers", to: "/real-teachers" },
      { label: "Guides", to: "/guides" },
    ],
  },
  {
    id: "teacher", label: "Teacher", short: "Teacher",
    promise: "Start today's prepared lesson and see who needs help.",
    to: "/teachers/console?tab=today", status: "live",
    nav: [
      { label: "Today", to: "/teachers/console?tab=today" },
      { label: "Class", to: "/teachers/console?tab=class" },
      { label: "Intervene", to: "/teachers/console?tab=intervene" },
      { label: "Plan", to: "/teachers/console?tab=plan" },
      { label: "Report", to: "/teachers/console?tab=report" },
      { label: "Build my week", to: "/teachers/build-week" },
      { label: "Guides", to: "/teachers/help" },
    ],
  },
  {
    id: "homeschool", label: "Homeschool", short: "Homeschool",
    promise: "Create a flexible Year → Term → Week → Day plan.",
    to: "/homeschool/planner", status: "live",
    nav: [
      { label: "Today", to: "/homeschool" },
      { label: "Planner", to: "/homeschool/planner" },
      { label: "Learning", to: "/grown-ups/curriculum" },
      { label: "Progress", to: "/homeschool/report" },
      { label: "Printables", to: "/homeschool/print" },
    ],
  },
  {
    id: "tutor", label: "Tutor", short: "Tutor",
    promise: "Prepare a focused session using relevant learning evidence.",
    to: "/tutor/console", status: "live",
    nav: [
      { label: "Learners", to: "/tutor/console" },
      { label: "Sessions", to: "/real-teachers" },
      { label: "Evidence", to: "/grown-ups/progress" },
      { label: "Guides", to: "/guides" },
    ],
  },
  {
    id: "school", label: "School leader", short: "School",
    promise: "See learning health, curriculum progress and Autopilot.",
    to: "/schools/admin", status: "beta",
    nav: [
      { label: "Overview", to: "/schools/admin" },
      { label: "Curriculum", to: "/curriculum/coverage" },
      { label: "Reports", to: "/schools/admin" },
      { label: "Set up a school", to: "/schools/setup" },
      { label: "About Schools", to: "/schools" },
    ],
  },
];

export const roleById = (id) => ROLES.find((r) => r.id === id) || null;

const ROLE_KEY = "mwq_grownup_role_v1";
export function getSavedRole() { try { return sessionStorage.getItem(ROLE_KEY) || ""; } catch { return ""; } }
export function saveRole(id) { try { sessionStorage.setItem(ROLE_KEY, id); } catch { /* ignore */ } }

/** Infer the role from a path so adult pages can show the right nav even on deep links. */
export function roleForPath(path) {
  const p = (path || "").split("?")[0];
  if (p.startsWith("/teachers") || p.startsWith("/teacher")) return "teacher";
  if (p.startsWith("/homeschool")) return "homeschool";
  if (p.startsWith("/tutor")) return "tutor";
  if (p.startsWith("/schools")) return "school";
  if (p.startsWith("/parents") || p.startsWith("/printables") || p.startsWith("/real-teachers")) return "parent";
  return getSavedRole() || "";
}
